/**
 * AdSense Banner Component
 * 
 * IMPORTANT: Before using this component, you need to:
 * 1. Replace 'ca-pub-XXXXXXXXXXXXXXXX' with your actual AdSense Publisher ID
 *    (found in your AdSense account under Settings > Account Information)
 * 2. Replace slot IDs ('1234567890', '0987654321') with your actual AdSense ad slot IDs
 *    (create ad units in AdSense and use the slot ID from the ad code)
 * 3. Make sure AdSense is approved for your domain
 * 
 * To configure:
 * - Update the data-ad-client attribute in the <ins> tag (line ~90)
 * - Update the slot prop values in CourseDetailPage.tsx
 * 
 * Usage:
 * <AdSenseBanner slot="your-slot-id" format="vertical" position="left" />
 */
import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledAdContainer = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: 80,
  width: '100%',
  minHeight: '250px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
  border: '1px solid rgba(148, 163, 184, 0.1)',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
    pointerEvents: 'none',
  },
}));

const AdLabel = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  left: theme.spacing(1),
  fontSize: '0.7rem',
  color: theme.palette.text.disabled,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  zIndex: 1,
}));

interface AdSenseBannerProps {
  slot?: string;
  format?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  style?: React.CSSProperties;
  className?: string;
  position?: 'left' | 'right';
}

const AdSenseBanner: React.FC<AdSenseBannerProps> = ({
  slot = '1234567890',
  format = 'auto',
  style,
  className,
  position = 'left',
}) => {
  const adRef = React.useRef<HTMLDivElement>(null);
  const insRef = React.useRef<HTMLElement | null>(null);
  const isInitialized = React.useRef<boolean>(false);

  // Load AdSense script if not already loaded
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      // Check if script already exists
      const existingScript = document.querySelector('script[src*="adsbygoogle"]');
      
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5546951103758500';
        script.async = true;
        script.crossOrigin = 'anonymous';
        document.head.appendChild(script);
      }
    }
  }, []);

  // Initialize AdSense ad - only once per component instance
  React.useEffect(() => {
    // Skip if already initialized
    if (isInitialized.current) {
      return;
    }

    // Wait for AdSense script to load and DOM to be ready
    const initializeAd = () => {
      if (typeof window === 'undefined' || !(window as any).adsbygoogle) {
        return false;
      }

      // Find the ins element for this component instance
      const insElement = insRef.current;
      if (!insElement) {
        return false;
      }

      // Check if this specific ins element has already been initialized
      // AdSense adds a data-adsbygoogle-status attribute when initialized
      if (insElement.hasAttribute('data-adsbygoogle-status')) {
        isInitialized.current = true;
        return true;
      }

      // Prevent double initialization
      if (isInitialized.current) {
        return true;
      }

      try {
        // Initialize the ad only if it hasn't been initialized
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
        isInitialized.current = true;
        return true;
      } catch (err) {
        // Check if error is about already initialized ads
        if (err instanceof Error && err.message.includes('already have ads')) {
          isInitialized.current = true;
          return true;
        }
        console.error('AdSense initialization error:', err);
        return false;
      }
    };

    // Wait for the script to load and DOM to be ready
    let retryCount = 0;
    const maxRetries = 10;
    
    const checkAndInit = () => {
      if (initializeAd()) {
        return; // Successfully initialized
      }
      
      retryCount++;
      if (retryCount < maxRetries && insRef.current) {
        // Retry after a short delay if script isn't loaded yet
        setTimeout(checkAndInit, 200);
      }
    };

    const timer = setTimeout(checkAndInit, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [slot]); // Re-run only if slot changes

  return (
    <StyledAdContainer
      elevation={0}
      className={className}
      sx={{
        ...style,
        [position === 'left' ? 'mr' : 'ml']: { xs: 0, md: 3 },
        display: { xs: 'none', lg: 'flex' }, // Hide on mobile and tablet, show on desktop
      }}
    >
      <AdLabel>Advertisement</AdLabel>
      <Box
        ref={adRef}
        sx={{
          width: '100%',
          minHeight: '250px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 0,
        }}
      >
        {/* AdSense Ad Unit */}
        <ins
          ref={(el) => {
            insRef.current = el;
          }}
          className="adsbygoogle"
          style={{
            display: 'block',
            width: '100%',
            minHeight: '250px',
          }}
          data-ad-client="ca-pub-5546951103758500"
          data-ad-slot={slot}
          data-ad-format={format}
          data-full-width-responsive="true"
        />
      </Box>
    </StyledAdContainer>
  );
};

export default AdSenseBanner;

